import config from 'config';
import pinterpolate from 'pinterpolate';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router';
import { Switch } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import {
  Overlay,
  QuickLink,
  SecondaryNav,
  useQuickLink,
  NotificationBar,
  QuickLinksWrapper,
  SearchBar,
  Header as VyagutaHeader,
  Global as VyagutaWrapper,
  AvatarMenu,
  HeaderNav,
  SidebarMenu,
  User as VyagutaUser,
  SearchBarProps,
} from '@vyaguta/vyaguta-ui';

import { addClassToChildren } from 'utils/dom';
import { handleError } from 'utils/errorHandler';

import * as authService from 'services/auth';
import { trackEvent } from 'services/mixPanel';

import { NOTICE_BOARD } from 'constants/routes';
import { mixPanelEvents } from 'constants/mixPanel';
import { USER_PROFILE_LIST_URL } from 'constants/endpoints';
import { MAC_SHORTCUT_KEY, ROLE, WIN_SHORTCUT_KEY } from 'constants/appConstants';
import { getHeaderNavs, getSecondaryNav, SUB_CONTRACTOR_HEADER } from 'constants/navigation';

import Notices from 'components/common/notices/Notices';
import AskUsAnything from 'components/common/ask-us-anything/AskUsAnything';

import { useFetchQuickLinks } from 'hooks/query/useQuickLinksQuery';
import { useUserPermissions, useUserReminders } from 'hooks/query/useUserInfo';
import useEmployeeDebounceWithLazyLoad from 'hooks/useEmployeeDebounceWithLazyLoad';

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

type LoggedInUser = {
  id: number | string;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  designation?: { name: string };
  [key: string]: JsonValue;
};

type HeaderOwnProps = {
  user: LoggedInUser;
  noticeCount?: number;
  isNoticeCountLoading?: boolean;
  userRoles?: string[];
  isUserRoleLoading?: boolean;
};

type Props = HeaderOwnProps & RouteComponentProps;

const Header = (props: Props) => {
  const { user: loggedInUser, history, noticeCount, isNoticeCountLoading, userRoles, isUserRoleLoading } = props;
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const reminders = useUserReminders();
  const permissions = useUserPermissions();

  const normalizedReminders = useMemo(
    () =>
      reminders.map(({ startDate: _startDate, ...r }) => ({
        ...r,
        redirectUrl: r.redirectUrl ?? '',
        endDate: new Date(r.endDate),
      })),
    [reminders]
  );

  const isSubContractor = userRoles?.includes(ROLE.SUB_CONTRACTOR);

  const user: VyagutaUser = useMemo(
    () => ({
      ...loggedInUser,
      name: `${loggedInUser.firstName} ${loggedInUser.lastName}`,
      image: loggedInUser.avatarUrl,
      designation: loggedInUser.designation,
    }),
    [loggedInUser]
  );

  const headerNavList = useMemo(() => {
    const baseHeaderNav = getHeaderNavs(history.location.pathname) as Array<{
      label: string;
      link?: string;
      isActive?: boolean;
      isDisabled?: boolean;
    }>;

    const headerNav: HeaderNav[] = baseHeaderNav.map((nav) => ({
      ...nav,
      user,
    }));

    if (isUserRoleLoading) {
      return [];
    }

    if (isSubContractor) {
      return headerNav.filter((item) => SUB_CONTRACTOR_HEADER.includes(item.label));
    }

    return headerNav;
  }, [history.location.pathname, isSubContractor, isUserRoleLoading, user]);

  const secondaryNavItems = useMemo(() => {
    const rawItems = getSecondaryNav(isSubContractor) as Array<{
      label: string;
      to?: string;
      exact?: boolean;
      allowFor?: string[];
      requiredAuthorization?: boolean;
    }>;

    return rawItems
      .filter((item) => !item.allowFor || item.allowFor.some((permission) => permissions[permission]))
      .map((item) => ({
        label: item.label,
        to: item.to,
        exact: item.exact,
      }));
  }, [isSubContractor, permissions]);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);

      await authService.logout();

      authService.redirectToLogin();
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const avatarMenus: AvatarMenu[] = [
    {
      type: 'link',
      props: {
        label: 'View Profile',
        href: getUserProfileUrl(loggedInUser),
      },
    },
    {
      type: 'button',
      props: {
        label: isLoggingOut ? 'Logging out' : 'Logout',
        onClick: handleLogout,
        disabled: isLoggingOut,
      },
    },
  ];

  const sidebarMenus: SidebarMenu[] = [
    {
      type: 'link',
      props: {
        label: 'View Profile',
        href: getUserProfileUrl(loggedInUser),
      },
      position: 'body',
    },
    {
      type: 'button',
      props: {
        label: isLoggingOut ? 'Logging out' : 'Logout',
        onClick: handleLogout,
        disabled: isLoggingOut,
      },
      position: 'footer',
    },
  ];

  useEffect(() => {
    const header = document.querySelector('#header');

    if (header) {
      addClassToChildren(header, 'flex-fix', (el) => {
        if (!(el instanceof Element)) return false;
        return window.getComputedStyle(el).display === 'flex';
      });
    }
  }, []);

  const {
    quickLinksButtonRef,
    isQuickLinksOpen,
    setIsQuickLinksOpen,
    handleQuickLinkMenuClick,
    topPosition,
    overlayRef,
    reminderRef,
    headerRef,
  } = useQuickLink();

  type QuickLinkData = React.ComponentProps<typeof QuickLinksWrapper>['linkLibraryData'][number];

  const [hasErrorInLoadingQuickLinks, setHasErrorInLoadingQuickLinks] = useState(false);
  const lastHandledQuickLinksErrorAtRef = useRef(0);

  const {
    data: fetchedQuickLinks,
    isLoading: isQuickLinksLoading,
    isError: isQuickLinksError,
    error: quickLinksError,
    errorUpdatedAt: quickLinksErrorUpdatedAt,
  } = useFetchQuickLinks<QuickLinkData[]>();

  const quickLinks = useMemo(() => fetchedQuickLinks ?? [], [fetchedQuickLinks]);

  useEffect(() => {
    if (!isQuickLinksError || !quickLinksError || quickLinksErrorUpdatedAt <= lastHandledQuickLinksErrorAtRef.current) {
      return;
    }

    handleError(quickLinksError);
    setHasErrorInLoadingQuickLinks(true);
    lastHandledQuickLinksErrorAtRef.current = quickLinksErrorUpdatedAt;
  }, [isQuickLinksError, quickLinksError, quickLinksErrorUpdatedAt]);

  useEffect(() => {
    if (isQuickLinksOpen) {
      trackEvent(mixPanelEvents.linkLibrary);
    }
  }, [isQuickLinksOpen]);

  const userDebounceOptions = useEmployeeDebounceWithLazyLoad({
    employeeId: null,
    additionalFetchProps: {},
    searchUserByFullName: true,
  }) as SearchBarProps['debounceOptions'];

  return (
    <div className="flex-fix" id="header">
      <VyagutaWrapper>
        <div ref={reminderRef}>
          <NotificationBar
            notifications={normalizedReminders}
            event={{
              startDate: config.events.startDate,
              endDate: config.events.endDate,
              link: config.events.link,
              title: config.events.title,
            }}
          />
        </div>
        <div ref={headerRef}>
          <VyagutaHeader
            headerNavs={headerNavList}
            avatarMenus={avatarMenus}
            user={user}
            sidebarMenus={sidebarMenus}
            isSidebarOpen={false}
          >
            {isUserRoleLoading && (
              <VyagutaHeader.Left>
                <div className="d-flex align-items-center justify-content-center gap-x-24 mlpx-16">
                  <SkeletonTheme baseColor="#e9edf0" highlightColor="#f0f3f5">
                    {Array(7)
                      .fill(0)
                      .map((item, index) => (
                        <Skeleton key={index} height={18} width={55} borderRadius={10} />
                      ))}
                  </SkeletonTheme>
                </div>
              </VyagutaHeader.Left>
            )}

            <VyagutaHeader.Right>
              <div className="mr-16">
                <div className="header-content">
                  <div className="mr-4x">
                    <SearchBar
                      debounceOptions={userDebounceOptions}
                      userProfileLink={USER_PROFILE_LIST_URL}
                      macShortcutKey={MAC_SHORTCUT_KEY}
                      winShortcutKey={WIN_SHORTCUT_KEY}
                      bodyOpenClassName="lf-overlay-body--hidden"
                      popOverClassName="popover-tippy"
                    />
                  </div>
                  <AskUsAnything />

                  <div ref={quickLinksButtonRef}>
                    <QuickLink isQuickLinksOpen={isQuickLinksOpen} onClick={handleQuickLinkMenuClick} />
                  </div>

                  <Notices
                    active={history.location.pathname === NOTICE_BOARD}
                    noticeCount={noticeCount}
                    isNoticeCountLoading={isNoticeCountLoading}
                  />
                </div>
              </div>
            </VyagutaHeader.Right>
          </VyagutaHeader>
        </div>

        <Overlay
          isOpen={isQuickLinksOpen}
          onClose={() => setIsQuickLinksOpen(false)}
          parentScroll={false}
          topPosition={topPosition}
        >
          <div ref={overlayRef}>
            <QuickLinksWrapper
              linkLibraryData={quickLinks}
              isLoading={isQuickLinksLoading}
              hasErrorInLoadingQuickLinks={hasErrorInLoadingQuickLinks}
            />
          </div>
        </Overlay>

        {props.location.pathname !== '/' && props.location.pathname !== NOTICE_BOARD && (
          <Switch>
            <SecondaryNav navItems={secondaryNavItems} />
          </Switch>
        )}
      </VyagutaWrapper>
    </div>
  );
};

/**
 * Gets users lms profiles url.
 *
 * @param {*} user
 * @returns
 */
const getUserProfileUrl = (user: { id: number | string } | null | undefined) => {
  return user ? pinterpolate(config.endpoints.lms.userProfile, { id: user.id }) : '';
};

export default withRouter(Header);
